/**
 * Payment Flow Helper
 * Orchestrates invoice creation and payment initiation for pending bookings
 */

import billingService from "../services/billingService";

import logger from "./logger";
import toastUtils from "./toast";
import { getNormalizedDurationType } from "./bookingTimeframeUtils";
import { calculateAddonItemPrice } from "./addonPricingUtils";
import { getAnalytics, logEvent } from "firebase/analytics";

// Helper to log debug events to Firebase Analytics
const logInvoiceDebug = (eventName, data) => {
  try {
    const analytics = getAnalytics();
    if (analytics) {
      logEvent(analytics, eventName, {
        ...data,
        timestamp: Date.now(),
        source: "paymentFlowHelper",
      });
    }
  } catch (e) {
    logger.log("Firebase Analytics not available for logging");
  }
  // Also log to console for local debugging
  logger.log(`🔍 INVOICE DEBUG [${eventName}]:`, data);
};

// Helper to log amount calculation details for debugging discrepancies
const logAmountCalculation = (context, details) => {
  const logData = {
    ...details,
    timestamp: Date.now(),
    context,
    source: "paymentFlowHelper",
  };

  // Log to Firebase Analytics
  logInvoiceDebug(`amount_calculation_${context}`, logData);

  // Enhanced console logging for debugging
  logger.log(`💰 AMOUNT CALCULATION [${context}]:`, {
    ratePrice: details.ratePrice,
    bookingUnits: details.bookingUnits,
    unitQuantity: details.unitQuantity,
    totalAmount: details.totalAmount,
    durationType: details.durationType,
    startTime: details.startTime,
    endTime: details.endTime,
    spaceId: details.spaceId,
    spaceName: details.spaceName,
  });
};

/**
 * Calculate duration in hours between start and end times
 * @param {string} startTime - UTC ISO string
 * @param {string} endTime - UTC ISO string
 * @returns {number} Duration in hours
 */
const _calculateDurationHours = (startTime, endTime) => {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const durationMs = end - start;
  return Math.ceil(durationMs / (1000 * 60 * 60)); // Round up to nearest hour
};

/**
 * Calculate booking duration in appropriate units based on space duration type
 * @param {string} startTime - UTC ISO string
 * @param {string} endTime - UTC ISO string
 * @param {string} durationType - HOURLY, DAILY, WEEKLY, MONTHLY
 * @returns {number} Duration in appropriate units (hours, days, weeks, months)
 */
const calculateBookingUnits = (startTime, endTime, durationType) => {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const durationMs = end - start;

  // 🔍 CONSOLE LOG: Calculate booking units
  logger.log("📏 CALCULATE BOOKING UNITS:", {
    startTime,
    endTime,
    durationType,
    startLocal: start.toLocaleString(),
    endLocal: end.toLocaleString(),
    durationMs,
    durationDays: durationMs / (1000 * 60 * 60 * 24),
  });

  switch (durationType?.toUpperCase()) {
    case "HOURLY":
      const hours = Math.ceil(durationMs / (1000 * 60 * 60));
      logger.log("📏 CALCULATE BOOKING UNITS RESULT: HOURLY =", hours);
      return hours;
    case "DAILY":
      const days = Math.ceil(durationMs / (1000 * 60 * 60 * 24));
      logger.log("📏 CALCULATE BOOKING UNITS RESULT: DAILY =", days);
      return days;
    case "WEEKLY":
      const weeks = Math.ceil(durationMs / (1000 * 60 * 60 * 24 * 7));
      logger.log("📏 CALCULATE BOOKING UNITS RESULT: WEEKLY =", weeks);
      return weeks;
    case "MONTHLY":
      // Calculate months more accurately using 30-day approximation to match UI
      const startDateMonthly = new Date(start);
      const endDateMonthly = new Date(end);
      const durationMsMonthly = end - start;
      const daysMonthly = durationMsMonthly / (1000 * 60 * 60 * 24);

      // Use 30-day month approximation to match UI calculation
      // This ensures consistency: 30 days = 1 month, 60 days = 2 months, etc.
      const monthsByDays = Math.max(1, Math.ceil(daysMonthly / 30));

      // 🔍 CONSOLE LOG: Monthly calculation breakdown
      logger.log("📏 CALCULATE BOOKING UNITS - MONTHLY BREAKDOWN:", {
        startYear: startDateMonthly.getFullYear(),
        startMonth: startDateMonthly.getMonth(),
        startDay: startDateMonthly.getDate(),
        endYear: endDateMonthly.getFullYear(),
        endMonth: endDateMonthly.getMonth(),
        endDay: endDateMonthly.getDate(),
        durationDays: daysMonthly,
        monthsBy30Days: monthsByDays,
        formula: `Math.max(1, Math.ceil(${daysMonthly.toFixed(2)} / 30)) = ${monthsByDays}`,
      });

      logger.log("📏 CALCULATE BOOKING UNITS RESULT: MONTHLY =", monthsByDays);
      return monthsByDays;
    default:
      // Fallback to hours if duration type is unknown
      const fallbackHours = Math.ceil(durationMs / (1000 * 60 * 60));
      logger.log("📏 CALCULATE BOOKING UNITS RESULT: FALLBACK (UNKNOWN TYPE) =", fallbackHours);
      return fallbackHours;
  }
};

/**
 * Initiate payment flow for a pending booking
 * Steps:
 * 1. Create invoice with booking reference
 * 2. Initiate payment with invoice reference
 * 3. Redirect to payment gateway
 *
 * @param {Object} booking - Booking object from createBookingPendingPayment response
 * @param {Object} bookingSpace - Space/workspace object being booked
 * @returns {Promise<{ success: boolean, checkoutUrl?: string }>}
 */

/**
 * Get currency ID from Redux persisted state based on currency code
 * Falls back to API fetch if not in Redux
 * @param {string} currencyCode - Currency code (e.g., 'GHS', 'KES', 'NGN')
 * @returns {Promise<number>} Currency ID
 */
const getCurrencyId = async currencyCode => {
  try {
    const upperCode = currencyCode?.toUpperCase();
    if (!upperCode) {
      throw new Error("Currency code is required");
    }

    // Try to get from Redux persisted state (redux-persist stores in localStorage)
    try {
      const persistedState = localStorage.getItem("persist:root");
      if (persistedState) {
        const parsed = JSON.parse(persistedState);
        if (parsed.dashboard) {
          const dashboardState = JSON.parse(parsed.dashboard);
          const currencies = dashboardState?.currencies || [];
          if (Array.isArray(currencies) && currencies.length > 0) {
            const currency = currencies.find(c => c.code?.toUpperCase() === upperCode);
            if (currency?.id) {
              logger.log(`Found ${upperCode} currency ID from Redux persisted state:`, currency.id);
              return currency.id;
            }
          }
        }
      }
    } catch (storeError) {
      logger.log("Redux persisted state not available, fetching from API");
    }

    // If not in Redux, fetch from API
    logger.log(`${upperCode} currency not in Redux, fetching from API...`);
    const response = await billingService.getCurrencies({ code: upperCode, limit: 1 });

    if (response?.data?.data && response.data.data.length > 0) {
      const currency = response.data.data[0];
      logger.log(`Found ${upperCode} currency ID from API:`, currency.id);
      return currency.id;
    }

    // If still not found, try to find it in a larger list
    const allCurrenciesResponse = await billingService.getCurrencies({ limit: 500 });
    if (allCurrenciesResponse?.data?.data) {
      const currency = allCurrenciesResponse.data.data.find(c => c.code?.toUpperCase() === upperCode);
      if (currency?.id) {
        logger.log(`Found ${upperCode} currency ID from full list:`, currency.id);
        return currency.id;
      }
    }

    throw new Error(`${upperCode} currency not found. Please ensure currencies are loaded.`);
  } catch (error) {
    logger.error(`Error fetching ${currencyCode} currency:`, error);
    throw new Error(`Failed to get ${currencyCode} currency: ${error.message}`);
  }
};

export const initiateBookingPayment = async (booking, bookingSpace, unitQuantity = 1) => {
  try {
    const currentUser = JSON.parse(localStorage.getItem("user") || localStorage.getItem("currentUser"));
    const organizationId = currentUser?.organizationId || currentUser?.organization?.id;

    // Attempt multiple paths to find the host organization ID (providerId)
    const providerIdFromSpace =
      bookingSpace?.organizationId ||
      bookingSpace?.organization?.id ||
      bookingSpace?.property?.organizationId ||
      bookingSpace?.property?.organization?.id ||
      bookingSpace?.building?.organizationId ||
      bookingSpace?.building?.organization?.id ||
      bookingSpace?.location?.buildingAddress?.organizationId ||
      bookingSpace?.location?.buildingAddress?.organization?.id ||
      booking?.hostOrganizationId ||
      booking?.hostOrganization?.id ||
      booking?.organizationId ||
      null;

    logger.log("DEBUG: paymentFlowHelper - booking:", booking);
    logger.log("DEBUG: paymentFlowHelper - bookingSpace:", bookingSpace);
    logger.log("DEBUG: paymentFlowHelper - identified providerId:", providerIdFromSpace);

    if (!organizationId) {
      throw new Error("Organization ID not found");
    }
    if (!providerIdFromSpace) {
      throw new Error("Space organization ID (providerId) not found. Please check console for data structure.");
    }

    // Get currency ID from space object (e.g., 'GHS', 'KES', 'NGN')
    const spaceCurrencyCode = bookingSpace?.currency || "KES";
    logger.log("Space currency code:", spaceCurrencyCode);

    const currencyId = await getCurrencyId(spaceCurrencyCode);
    if (!currencyId) {
      throw new Error(`${spaceCurrencyCode} currency not found. Please contact support.`);
    }

    // Calculate duration for pricing based on space duration type - UNIFIED with UI
    const durationType = getNormalizedDurationType(bookingSpace);
    const bookingUnits = calculateBookingUnits(booking.startTime, booking.endTime, durationType);

    // UNIFIED Price Field Selection: propertyPrice.price || price || ratePrice
    const ratePrice =
      bookingSpace?.propertyPrice?.price || bookingSpace?.price || bookingSpace?.ratePrice || bookingSpace?.amount || 0;

    const totalAmount = ratePrice * bookingUnits * unitQuantity;

    // 🔍 CRITICAL LOGGING: Log amount calculation at the invoice creation point
    logAmountCalculation("initiateBookingPayment_entry", {
      ratePrice,
      bookingUnits,
      unitQuantity,
      totalAmount,
      durationType,
      startTime: booking.startTime,
      endTime: booking.endTime,
      spaceId: bookingSpace?.id,
      spaceName: bookingSpace?.name || bookingSpace?.title,
      bookingId: booking?.id,
      priceSource: bookingSpace?.propertyPrice?.price
        ? "propertyPrice.price"
        : bookingSpace?.price
          ? "price"
          : bookingSpace?.ratePrice
            ? "ratePrice"
            : bookingSpace?.amount
              ? "amount"
              : "default_0",
      // Log the raw space object price fields for debugging
      rawPriceFields: {
        propertyPricePrice: bookingSpace?.propertyPrice?.price,
        spacePrice: bookingSpace?.price,
        ratePrice: bookingSpace?.ratePrice,
        amount: bookingSpace?.amount,
      },
    });

    // Get additional space details for better description
    const spaceName = bookingSpace.name || bookingSpace.description || "Workspace";
    const capacity =
      bookingSpace.capacity ||
      bookingSpace.details?.capacity ||
      bookingSpace.instances?.[0]?.listingDetails?.maxCapacity;

    // Get address/location info
    const getSpaceAddress = () => {
      const firstInstance = bookingSpace.instances?.[0];
      const buildingAddress = firstInstance?.location?.buildingAddress;
      if (buildingAddress?.streetAddress) {
        return buildingAddress.streetAddress;
      }
      if (firstInstance?.location?.name) {
        return firstInstance.location.name;
      }
      if (bookingSpace.location?.address) {
        return bookingSpace.location.address;
      }
      return null;
    };
    const address = getSpaceAddress();

    logger.log("Booking payment calculation:", {
      bookingId: booking.id,
      durationType,
      bookingUnits,
      ratePrice,
      totalAmount,
      currencyId,
      capacity,
      address,
    });

    // Build invoice item description with details
    let itemDescription = spaceName;
    if (capacity) {
      itemDescription += ` for ${capacity}${capacity === 1 ? " person" : " people"}`;
    }
    if (address) {
      itemDescription += ` at ${address}`;
    }

    // Add duration description based on type
    const unitName =
      durationType?.toLowerCase() === "monthly"
        ? "month"
        : durationType?.toLowerCase() === "weekly"
          ? "week"
          : durationType?.toLowerCase() === "daily"
            ? "day"
            : "hour";
    itemDescription += ` - ${unitQuantity} unit${unitQuantity !== 1 ? "s" : ""} (${bookingUnits} ${unitName}${bookingUnits !== 1 ? "s" : ""} each)`;

    // Step 1: Create invoice with booking ID as clientReference
    const invoiceItems = [
      {
        amount: totalAmount.toString(),
        unitPrice: (ratePrice * bookingUnits).toString(),
        quantity: unitQuantity,
        description: itemDescription,
      },
    ];

    // Add addons to invoice items if present in the booking
    if (Array.isArray(booking.addons) && booking.addons.length > 0) {
      const spaceAddons = bookingSpace?.addons || [];
      const spaceDurationType = getNormalizedDurationType(bookingSpace);

      booking.addons.forEach(addon => {
        const matchedSpaceAddon = spaceAddons.find(sa => (sa.id || sa.addonId) === (addon.addonId || addon.id));
        const mergedAddon = { ...(matchedSpaceAddon || {}), ...addon };
        const calculated = calculateAddonItemPrice(
          mergedAddon,
          addon.quantity || 1,
          { startTime: booking.startTime, endTime: booking.endTime },
          spaceDurationType,
          spaceCurrencyCode
        );

        const addonUnitPrice = calculated.unitPrice;
        const addonQuantity = calculated.quantity;
        const addonTotal = calculated.totalPrice;

        let addonDesc = `Addon: ${calculated.name}`;
        if (calculated.durationUnits > 1 && calculated.duration !== "FIXED") {
          addonDesc += ` - ${addonQuantity} unit${addonQuantity !== 1 ? "s" : ""} (${calculated.durationUnits} ${calculated.durationUnitLabel})`;
        } else {
          addonDesc += ` - ${addonQuantity} unit${addonQuantity !== 1 ? "s" : ""}`;
        }

        invoiceItems.push({
          amount: addonTotal.toString(),
          unitPrice: (addonUnitPrice * calculated.durationUnits).toString(),
          quantity: addonQuantity,
          description: addonDesc,
        });
      });
    }

    // Calculate final total including addons
    const finalTotalAmount = invoiceItems.reduce((sum, item) => sum + parseFloat(item.amount), 0);

    const invoicePayload = {
      organizationId: organizationId,
      providerId: providerIdFromSpace,
      clientReference: `${booking.id}`, // Boss requested: pass booking IDs as is for booking payments
      description: `Booking payment for ${spaceName} (${unitQuantity} unit${unitQuantity !== 1 ? "s" : ""})${booking.addons?.length > 0 ? " with addons" : ""}`,

      productType: "DIRECT_BOOKING",
      currencyId: currencyId, // Required: KES currency ID
      invoiceItems: invoiceItems,
      serviceDate: new Date(booking.startTime).toISOString().split("T")[0],
    };

    // 🔍 CRITICAL LOGGING: Log the exact invoice payload being sent
    logInvoiceDebug("invoice_payload_created", {
      invoicePayload,
      calculationBreakdown: {
        ratePrice,
        bookingUnits,
        unitQuantity,
        spaceTotal: totalAmount,
        finalTotalAmount,
        formula: `${ratePrice} (rate) × ${bookingUnits} (units) × ${unitQuantity} (quantity) = ${totalAmount}`,
      },
      bookingDetails: {
        bookingId: booking.id,
        startTime: booking.startTime,
        endTime: booking.endTime,
        spaceId: bookingSpace?.id,
        spaceName,
      },
    });

    logger.log("Creating invoice for booking:", invoicePayload);
    const invoiceResponse = await billingService.createInvoice(invoicePayload);

    if (!invoiceResponse.data?.data?.referenceCode) {
      logger.error("Invoice response missing referenceCode:", invoiceResponse);
      throw new Error("Invoice reference not returned");
    }

    const invoiceReference = invoiceResponse.data.data.referenceCode;
    logger.log("Invoice created successfully with reference:", invoiceReference);

    // Step 2: Initiate payment with invoice reference only
    const paymentPayload = { reference: invoiceReference };

    logger.log("Initiating payment with payload:", paymentPayload);
    const paymentResponse = await billingService.initiatePayment(paymentPayload);

    logger.log("Payment initiation response:", paymentResponse);

    // Step 3: Handle payment response
    if (paymentResponse.data?.data?.checkoutUrl) {
      const checkoutUrl = paymentResponse.data.data.checkoutUrl;
      logger.log("Opening checkout URL in new tab:", checkoutUrl);
      window.open(checkoutUrl, "_blank");

      return {
        success: true,
        checkoutUrl: checkoutUrl,
        invoiceReference: invoiceReference,
        transactionId: paymentResponse.data.data.transactionId,
      };
    } else {
      logger.log("No checkout URL in payment response");
    }

    // If no checkout URL, assume payment was initiated successfully
    return {
      success: true,
      paymentResponse: paymentResponse.data,
      invoiceReference: invoiceReference,
    };
  } catch (error) {
    logger.error("Payment flow error:", error);

    // Show user-friendly error
    const errorMessage = error.response?.data?.message || error.message || "Could not initiate payment";
    toastUtils.error("Payment initiation failed", {
      description: errorMessage,
    });

    throw error;
  }
};

const paymentFlowHelper = {
  initiateBookingPayment,
};

export default paymentFlowHelper;
